import { Helmet } from 'react-helmet-async';

interface SchemaMarkupProps {
  schema: Record<string, any> | Record<string, any>[];
}

export function SchemaMarkup({ schema }: SchemaMarkupProps) {
  const schemas = Array.isArray(schema) ? schema : [schema];

  return (
    <Helmet>
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

// Reusable schema generators
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'RevOps AI',
  url: 'https://revops.digitais.ai',
  logo: 'https://revops.digitais.ai/revopsai-icon.png',
  description: 'Revenue Intelligence platform that transforms CRM data into predictable revenue',
  foundingDate: '2026',
  founders: [
    { '@type': 'Person', name: 'Filipi Simas', url: 'https://www.linkedin.com/in/filipisimas/' },
    { '@type': 'Person', name: 'Juliano Bellé', url: 'https://www.linkedin.com/in/julianobelle/' },
  ],
  sameAs: [
    'https://www.linkedin.com/company/revopsai',
    'https://twitter.com/RevOpsAI',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    availableLanguage: ['Portuguese', 'English'],
  },
};

export const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'RevOps AI',
  url: 'https://revops.digitais.ai',
  description: 'Revenue Intelligence platform for predictable revenue from CRM data',
  inLanguage: ['pt-BR', 'en'],
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://revops.digitais.ai/blog?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

export function generateSoftwareAppSchema(language: string) {
  const isPt = language?.startsWith('pt');
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'RevOps AI',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: isPt
      ? 'Plataforma de Revenue Intelligence que conecta com HubSpot e RD Station para entregar diagnósticos de funil, forecast matemático e Revenue Playbooks'
      : 'Revenue Intelligence platform that connects with HubSpot and RD Station to deliver funnel diagnostics, mathematical forecasting, and Revenue Playbooks',
    url: 'https://revops.digitais.ai',
    inLanguage: language,
    offers: isPt
      ? [
          { '@type': 'Offer', name: 'Starter', price: '497', priceCurrency: 'BRL', priceValidUntil: '2026-12-31', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Growth', price: '997', priceCurrency: 'BRL', priceValidUntil: '2026-12-31', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Pro', price: '1997', priceCurrency: 'BRL', priceValidUntil: '2026-12-31', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Enterprise', price: '0', priceCurrency: 'BRL', description: 'Preço sob consulta', availability: 'https://schema.org/PreOrder' },
        ]
      : [
          { '@type': 'Offer', name: 'Starter', price: '99', priceCurrency: 'USD', priceValidUntil: '2026-12-31', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Growth', price: '249', priceCurrency: 'USD', priceValidUntil: '2026-12-31', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Pro', price: '499', priceCurrency: 'USD', priceValidUntil: '2026-12-31', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'Enterprise', price: '0', priceCurrency: 'USD', description: 'Contact for pricing', availability: 'https://schema.org/PreOrder' },
        ],
    featureList: [
      'Revenue Intelligence Dashboard',
      'Sales Analytics',
      'AI-Powered Forecast',
      'Revenue Playbooks',
      'CRM Integration (HubSpot, RD Station)',
      'Pipeline Analysis',
      'What-If Revenue Simulator',
    ],
  };
}

// Keep legacy export for backward compat
export const softwareAppSchema = generateSoftwareAppSchema('pt-BR');

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function generateArticleSchema(article: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  slug: string;
  inLanguage?: string;
}) {
  // Ensure ISO 8601 date format
  const ensureISO = (d: string) => d.includes('T') ? d : `${d}T00:00:00Z`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    description: article.description,
    image: [article.image],
    datePublished: ensureISO(article.datePublished),
    dateModified: ensureISO(article.dateModified),
    inLanguage: article.inLanguage || 'pt-BR',
    author: { '@type': 'Organization', name: 'RevOps AI' },
    publisher: {
      '@type': 'Organization',
      name: 'RevOps AI',
      logo: { '@type': 'ImageObject', url: 'https://revops.digitais.ai/revopsai-icon.png' },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://revops.digitais.ai/${article.slug}`,
    },
  };
}
